import { AxiosResponse } from "axios";
import { PolicyState } from "../../features/policy/policySlice";
import advService from "../../util/advService";
import service from "../../util/axiosConfig";
import { BASE_URL } from "../../util/config";
import {
    EMPLOYEE_CUSTOMER_GET,
    MAKE_DROPDOWN_GET,
    MASTER_INSURER_LIST_GET,
    MODEL_DROPDOWN_GET,
    RE_SCAN_STAGING_PDF,
    SAVE_CUSTOMER_MEMBERS_POST,
    SAVE_POLICY_POST,
    UPLOAD_PDF_POST,
    VARIANT_DROPDOWN_GET,
} from "../../util/newconfig";
import {
    JavaResponse,
    PDFUploadResponse,
    PolicyMemberDTO,
    PolicySavePayloadDTO,
} from "../../types";

export interface PolicyFeatures {
    label: string;
    heading: string;
    subheading: string;
    text: string;
    page: number;
    richText: boolean;
}

type CustomerMemberDTO = {
    id: number;
    name: string;
    dob: string; // in "YYYY-MM-DD" format
    gender: "Male" | "Female" | "Other"; // Assuming fixed options
    relationship: string;
    uuid: string;
    active: boolean;
};

export const policyService = {
    employee: {
        getPolicyData: (user: { multipleLeads: Boolean }) => {
            return new Promise((resolve, reject) => {
                if (user) {
                    const url = `${BASE_URL}v1/policy/employee${
                        user.multipleLeads
                            ? "/" + sessionStorage.getItem("uuid")
                            : ""
                    }`;
                    service.get(url).then((res) => {
                        if (res?.data.success) {
                            resolve(res.data.response);
                            return;
                        }

                        reject(res.data.message);
                        throw new Error(res.data.message);
                    });
                } else {
                    reject("User not defined");
                }
            });
        },
    },
    getPolicyFeatures: (
        user: { userType: string },
        pid: string,
        uuid: string
    ): Promise<{ d_features: PolicyFeatures[] }> => {
        return new Promise((resolve, reject) => {
            if (user) {
                const url =
                    user.userType === "EMPLOYEE"
                        ? `${BASE_URL}v1/policy/employee/features/v3/${uuid}/${pid}`
                        : `${BASE_URL}v1/policy/features/v2/${pid}/${uuid}`;
                service.get(url).then((res) => {
                    if (res?.data.success) {
                        resolve(res.data.response);
                        return;
                    }
                    reject(res.data.message);
                    throw new Error(res.data.message);
                });
            } else {
                reject("User not defined");
            }
        });
    },
    getPolicyDetails: (
        pid: string,
        uuid: string
    ): Promise<PolicyState["policyDetailDto"]> => {
        return new Promise((resolve, reject) => {
            const url = `${BASE_URL}v1/employee/policy/v2/${uuid}/${pid}`;
            service.get(url).then((res) => {
                if (res?.data.success) {
                    resolve(res.data.response);
                    return;
                }
                reject(res.data.message);
                throw new Error(res.data.message);
            });
        });
    },
    getPolicyCards: (): Promise<PolicyState["policyCardDto"]> => {
        return new Promise((resolve, reject) => {
            const url = `${BASE_URL}v1/employee/policy/v2`;
            service.get(url).then((res) => {
                if (res?.data.success) {
                    resolve(res.data.response);
                    return;
                }
                reject(res.data.message);
                throw new Error(res.data.message);
            });
        });
    },
    uploadPersonalPolicyPDF: async (
        file: File,
        agentUuid?: string
    ): Promise<PDFUploadResponse[]> => {
        return new Promise((resolve, reject) => {
            const finalUuid = agentUuid || sessionStorage.getItem("uuid");

            if (!finalUuid) return reject("Agent uuid not found");

            const formData = new FormData();
            formData.append("file", file);

            advService(
                {
                    ...UPLOAD_PDF_POST,
                    url: UPLOAD_PDF_POST.url.replace("{agentUuid}", finalUuid),
                },
                formData,
                (
                    response: AxiosResponse<JavaResponse<PDFUploadResponse[]>>
                ) => {
                    if (response?.data?.success) {
                        resolve(response?.data?.response);
                        return;
                    }
                    reject(response);
                }
            );
        });
    },
    rescanPDF: async (
        agentUuid: string,
        stagingUuid: string
    ): Promise<PDFUploadResponse> => {
        let config = {
            ...RE_SCAN_STAGING_PDF,
            url: RE_SCAN_STAGING_PDF.url
                .replace("{agentUuid}", agentUuid)
                .replace("{stagingUuid}", stagingUuid),
        };

        return new Promise((resolve, reject) => {
            advService(
                config,
                null,
                (response: AxiosResponse<JavaResponse<PDFUploadResponse>>) => {
                    if (response.data.success) {
                        resolve(response?.data?.response);
                        return;
                    }
                    reject(response.data.message);
                }
            );
        });
    },
    getInsurerList: (
        agentUuid: string
    ): Promise<AxiosResponse<
        JavaResponse<{ label: string; value: string }[]>
    > | null> => {
        let config = {
            ...MASTER_INSURER_LIST_GET,
            url: MASTER_INSURER_LIST_GET.url.replace("{uuid}", agentUuid),
        };

        return new Promise((resolve, reject) => {
            advService(
                config,
                null,
                (
                    response: AxiosResponse<
                        JavaResponse<{ label: string; value: string }[]>
                    >
                ) => {
                    if (response.data.success) {
                        resolve(response);
                        return;
                    }
                    reject(response.data.message);
                }
            );
        });
    },
    getEmployeeCustomer: (): Promise<
        AxiosResponse<JavaResponse<{ label: string; value: string }>>
    > => {
        return new Promise((resolve, reject) => {
            advService(
                EMPLOYEE_CUSTOMER_GET,
                null,
                (
                    response: AxiosResponse<
                        JavaResponse<{ label: string; value: string }>
                    >
                ) => {
                    if (response.data.success) {
                        resolve(response);
                        return;
                    }
                    reject(response.data.message);
                }
            );
        });
    },
    savePersonalPolicy: (
        policyData: PolicySavePayloadDTO
    ): Promise<AxiosResponse<JavaResponse<PolicySavePayloadDTO>> | null> => {
        return new Promise((resolve, reject) => {
            advService(
                SAVE_POLICY_POST,
                policyData,
                (
                    response: AxiosResponse<JavaResponse<PolicySavePayloadDTO>>
                ) => {
                    if (response.data.success) {
                        resolve(response);
                        return;
                    }
                    reject(response.data.message);
                }
            );
        });
    },
    saveCustomerPolicyMembers: (
        members: {
            name: string;
            dob: string;
            relationship: string;
            gender: string;
        }[],
        clientUuid: string
    ): Promise<AxiosResponse<
        JavaResponse<{ d_client_members: CustomerMemberDTO[] }>
    > | null> => {
        return new Promise((resolve, reject) => {
            let config = {
                ...SAVE_CUSTOMER_MEMBERS_POST,
                url: SAVE_CUSTOMER_MEMBERS_POST.url.replace(
                    "{clientUuid}",
                    clientUuid
                ),
            };
            advService(
                config,
                members,
                (
                    response: AxiosResponse<
                        JavaResponse<{ d_client_members: CustomerMemberDTO[] }>
                    >
                ) => {
                    if (response.data.success) {
                        resolve(response);
                        return;
                    }
                    reject(response.data.message);
                }
            );
        });
    },
};

export type Option = {
    label: string;
    value: string;
};

export const getMakeOptions = (agentUuid: string): Promise<Option[]> => {
    return new Promise((resolve, reject) => {
        if (agentUuid) {
            const makeConfig = {
                ...MAKE_DROPDOWN_GET,
                url: MAKE_DROPDOWN_GET.url.replace("{agentUuid}", agentUuid),
            };
            advService(
                makeConfig,
                {},
                (res: AxiosResponse<JavaResponse<Option[]>>) => {
                    if (res?.data?.success) {
                        resolve(res.data.response);
                    } else {
                        resolve([]);
                    }
                }
            );
        }
    });
};

export const getModels = (
    agentUuid: string,
    make: string
): Promise<Option[]> => {
    return new Promise((resolve, reject) => {
        if (agentUuid && make) {
            const config = {
                ...MODEL_DROPDOWN_GET,
                url: MODEL_DROPDOWN_GET.url.replace("{agentUuid}", agentUuid),
            };

            config.url = config.url + `?${make}`;

            advService(
                config,
                {},
                (res: AxiosResponse<JavaResponse<Option[]>>) => {
                    if (res?.data?.success) {
                        resolve(res.data.response);
                    } else {
                        resolve([]);
                    }
                }
            );
        }
    });
};

export const getVariants = (
    agentUuid: string,
    model: string
): Promise<Option[]> => {
    return new Promise((resolve, reject) => {
        if (agentUuid && model) {
            const config = {
                ...VARIANT_DROPDOWN_GET,
                url: VARIANT_DROPDOWN_GET.url.replace("{agentUuid}", agentUuid),
            };

            config.url = config.url + `?${model}`;

            advService(
                config,
                {},
                (res: AxiosResponse<JavaResponse<Option[]>>) => {
                    if (res?.data?.success) {
                        resolve(res.data.response);
                    } else {
                        resolve([]);
                    }
                }
            );
        }
    });
};
